import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { BroadcastEventModel, BroadcastService, GridBaseComponent } from '@cv-invision/core';

import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ProductsService } from './service/products.service';
import { CustomActionProductsCellComponent } from './custom-grid-cell/custom-action-products-cell.component';
import { ProductsColumnsModel } from './model/product-column.model';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { Store } from '@ngxs/store';
import { SetProductList } from './state/actions/product.actions';
import { ProductState } from './state/product.state';
import { Router } from '@angular/router';

export const UPDATE_PRODUCT_LIST = 'UPDATE_PRODUCT_LIST';

@Component({
	selector: 'cv-products',
	templateUrl: './products.component.html',
	styleUrls: ['./products.component.scss']
})
export class ProductsComponent extends GridBaseComponent implements OnInit, OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	filterProducts = {
		title: '',
		pagination: {
			totalCount: 0,
			offset: 0,
			limit: 100000
		}
	};
	productsListTmp: Array<any>;
	private searchTextChange: EventEmitter<any> = new EventEmitter<any>();
	public searchText: string;

	constructor(
		private productService: ProductsService,
		private broadcastService: BroadcastService,
		private store: Store,
		private router: Router
	) {
		super();
	}

	ngOnInit(): void {
		this.gridOptions.columnDefs = new ProductsColumnsModel().columns;
		this.gridOptions.frameworkComponents = {
			customActionProductsCellComponent: CustomActionProductsCellComponent
		};

		const productList = this.store.selectSnapshot(ProductState.getProductList);

		if (productList) {
			this.searchText = productList.filter ? productList.filter : '';
			this.filterProducts.pagination.offset = productList.pagination ? productList.pagination : 0;
		}

		/**
		 * filter by title
		 */
		this.searchTextChange
			.pipe(
				distinctUntilChanged()
			)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event.property === 'title') {
					// eslint-disable-next-line no-useless-escape
					// this.searchText = event.value.replace(/[&\/\\#,+()$~%'":*?<>{}]/g, '') || '';
					this.searchText = event.value;
					this.filterProducts.title = event.value;
					this.loadGridData();
					this.saveProductListState();
				}
			});
		this.loadGridData();
		this.registerRefreshListListener();
		this.productService
			.getReloadListConfirmationListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result && result.reloadList) {
					this.loadGridData();
				}
			});
	}

	/**
	 *  Call service endpoint to search Products.
	 */
	loadGridData(): void {
		const offset = this.filterProducts.pagination.offset;
		this.filterProducts.pagination.offset = 0;
		this.filterProducts.pagination.totalCount = this.gridPagination.pagination.totalCount;
		this.productService
			.getProducts(this.filterProducts)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					if (result.data) {
						result.data = result.data.map((product: any) => {

							if (product.appScopeJobName) {
								product.appScopeJobName = `(${product.appScopeJobId}) ${product.appScopeJobName}`;
							}

							return product;
						});

						this.productsListTmp = result.data;
						this.filterProducts.pagination.offset = offset;

						if (this.searchText) {
							this.applyTitleFilter(0);
						} else {
							// Show only 10 records per page
							this.gridData = result.data.slice(offset, offset + 10);
							this.gridPagination.pagination.offset = offset;
							this.gridPagination.pagination.limit = 10;
							this.gridPagination.pagination.totalCount = result.data.length;
						}
					}
				},
				error => console.error(error)
			);
	}

	/**
	 * Address value has change
	 */
	onSearchTextChange(property: string, event: any): void {
		this.searchTextChange.emit({ property: property, value: event.value });
	}

	/**
	 * Register Lister to refresh product list
	 */
	private registerRefreshListListener(): void {
		this.broadcastService
			.onEvent(UPDATE_PRODUCT_LIST)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((e: BroadcastEventModel) => {
				this.loadGridData();
			});
	}

	/**
	 * Pagination of the products list
	 * @param paginationModel
	 */
	onPaginationChange(paginationModel: PaginationModel): void {
		this.filterProducts.pagination.offset = paginationModel.offset;

		this.saveProductListState();

		if (this.searchText) {
			this.applyTitleFilter(paginationModel.offset);
		} else {
			this.gridData = this.productsListTmp.slice(paginationModel.offset, paginationModel.offset + 10);
		}
	}

	/**
	 * Filter by title
	 * @param offset
	 */
	applyTitleFilter(offset: number = 0): void {
		this.gridPagination.pagination.offset = this.filterProducts.pagination.offset = offset;
		const result = this.productsListTmp.filter(product => product.title.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1);

		// Show only 10 records per page
		this.gridData = result.slice(offset, offset + 10);
		this.gridPagination.pagination.limit = 10;
		this.gridPagination.pagination.totalCount = result.length;

		this.saveProductListState();
	}

	/**
	 * Save product list state
	 */
	saveProductListState(): void {
		const productListStateModel = {
			pagination: this.filterProducts.pagination.offset,
			filter: this.searchText
		};

		this.store.dispatch(new SetProductList(productListStateModel));
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/***
	 * add new Product
	 */
	onAddProduct(): void {
		this.router.navigate([`/global-configuration/product/create/`]);
	}
}
